/**
 * The statistics footer of the Home doc-list, rendered by `DocListTable` under the
 * row window; it renders nothing until some column carries a statistic.
 *
 * Table mode lays one cell per visible column on the body's grid template, so each
 * statistic sits under its column. Card mode has no column tracks, so the footer is
 * a single sticky strip naming each column beside its value, scrolling sideways when
 * it overflows.
 */
import { Typography, theme } from 'antd';
import { ACTIONS_COLUMN_ID } from '../constants';

const { Text } = Typography;

export interface ComputedStatistic {
  type: 'count' | 'sum' | 'avg';
  value: number;
  formattedValue: string;
}

interface FooterColumn {
  id: string;
  header: string;
}

interface DocListTableFooterProps {
  gridTemplateColumns: string;
  visibleColumns: FooterColumn[];
  statistics: Record<string, ComputedStatistic>;
  frozenLeftOffsets: Map<string, number>;
  cardMode?: boolean;
}

function DocListTableFooter({
  gridTemplateColumns,
  visibleColumns,
  statistics,
  frozenLeftOffsets,
  cardMode = false,
}: DocListTableFooterProps) {
  const { token } = theme.useToken();

  const hasAnyStatistics = Object.keys(statistics).length > 0;
  if (!hasAnyStatistics) return null;

  if (cardMode) {
    return (
      <div
        role="row"
        aria-label="doclist-table-footer"
        style={{
          display: 'flex',
          flexWrap: 'nowrap',
          alignItems: 'center',
          gap: token.marginSM,
          overflowX: 'auto',
          minHeight: 32,
          paddingInline: token.paddingSM,
          position: 'sticky',
          bottom: 0,
          zIndex: 10,
          background: token.colorBgElevated,
          borderTop: `2px solid ${token.colorBorder}`,
          boxShadow: `0 -2px 8px ${token.colorBgTextHover}`,
        }}
      >
        {visibleColumns
          .filter(col => statistics[col.id])
          .map(col => (
            <div
              key={col.id}
              role="cell"
              aria-label={`footer-cell-${col.id}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: token.marginXXS,
                flexShrink: 0,
                whiteSpace: 'nowrap',
              }}
            >
              <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>{col.header}</Text>
              <Text style={{ fontSize: token.fontSizeSM }}>{statistics[col.id].formattedValue}</Text>
            </div>
          ))}
      </div>
    );
  }

  return (
    <div
      role="row"
      aria-label="doclist-table-footer"
      style={{
        display: 'grid',
        gridTemplateColumns,
        position: 'sticky',
        bottom: 0,
        zIndex: 10,
        background: token.colorBgElevated,
        borderTop: `2px solid ${token.colorBorder}`,
        boxShadow: `0 -2px 8px ${token.colorBgTextHover}`,
      }}
    >
      {visibleColumns.map(col => {
        const stat = statistics[col.id];
        const isActionsColumn = col.id === ACTIONS_COLUMN_ID;
        const leftOffset = frozenLeftOffsets.get(col.id);
        const isFrozen = leftOffset !== undefined;

        return (
          <div
            key={col.id}
            role="cell"
            aria-label={`footer-cell-${col.id}`}
            style={{
              padding: `${token.paddingXS}px ${token.paddingSM}px`,
              position: isFrozen || isActionsColumn ? 'sticky' : 'relative',
              left: isFrozen ? `${leftOffset}px` : undefined,
              right: isActionsColumn ? 0 : undefined,
              background: token.colorBgElevated,
              zIndex: isFrozen || isActionsColumn ? 5 : 1,
              boxShadow: isFrozen ? `inset -1px 0 0 ${token.colorBorder}` : undefined,
              minHeight: 32,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {stat ? (
              <Text style={{ fontSize: token.fontSizeSM }}>
                {stat.formattedValue}
              </Text>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export default DocListTableFooter;
